import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORT_BODY_MAX } from "../../utils/constants/support";

/**
 * Takes a message's bubble while its author rewrites the text.
 *
 * `onSave(body)` must return a promise. Resolved: the caller has the edited
 * thread on screen and closes the editor. Rejected: the editor stays open with
 * the draft, and the caller has said what went wrong. The same text again is
 * not an edit; an empty one is, only for a message that keeps its attachments
 * (`hasFiles`). Ctrl/Cmd+Enter saves, Escape cancels. `compact`: the Mini
 * App's narrow layout, without the keyboard hint.
 */
export default function MessageEditor({ initialBody, hasFiles, onSave, onCancel, compact = false }) {
  const { t } = useTranslation();
  const [body, setBody] = useState(initialBody || "");
  const [saving, setSaving] = useState(false);
  const alive = useRef(true);
  const textarea = useRef(null);

  useEffect(() => {
    alive.current = true;
    const field = textarea.current;
    if (field) {
      field.focus();
      // The caret after the text, as if the author had just typed it.
      field.setSelectionRange(field.value.length, field.value.length);
    }
    return () => {
      alive.current = false;
    };
  }, []);

  const trimmed = body.trim();
  const canSave = !saving && trimmed !== (initialBody || "").trim() && (trimmed.length > 0 || hasFiles);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(trimmed);
    } catch (_e) {
      /* kept the draft; the caller already said what went wrong */
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      save();
    } else if (event.key === "Escape" && !saving) {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="w-full rounded-2xl border border-[color:var(--support-mine-bg)] bg-[color:var(--support-surface)] p-3 space-y-2">
      {/* readOnly rather than disabled while saving: disabling drops the focus
          and, on a phone, the keyboard with it. */}
      <textarea
        ref={textarea}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={3}
        maxLength={SUPPORT_BODY_MAX}
        readOnly={saving}
        aria-label={t("support.thread.editLabel")}
        // border-0/p-0/focus:ring-0 undo @tailwindcss/forms, as in the Composer.
        className="w-full resize-y min-h-[72px] border-0 p-0 bg-transparent outline-none focus:ring-0 text-sm leading-relaxed text-[color:var(--support-surface-text)]"
      />
      <div className="flex flex-wrap items-center justify-end gap-2">
        {!compact && (
          <span className="mr-auto hidden sm:inline text-[11px] text-[color:var(--support-muted)]">
            {t("support.thread.editHint")}
          </span>
        )}
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-xl px-3 py-1.5 text-xs font-bold text-[color:var(--support-muted)] hover:text-[color:var(--support-surface-text)] disabled:opacity-50"
        >
          {t("support.thread.cancel")}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          className="rounded-xl px-3 py-1.5 text-xs font-bold bg-[color:var(--support-mine-bg)] text-[color:var(--support-mine-text)] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2"
        >
          {saving ? t("support.saving") : t("support.thread.save")}
        </button>
      </div>
    </div>
  );
}
